import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  ArrowLeftIcon,
  ChatBubble,
  ClassityLogo,
  DashboardIcon,
  FloatingControl,
  MoreIcon,
  Participants,
  Sheet,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { Stack, Typography } from '@mui/material';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { CircularProgress } from '@classity-edtech/ui-base';
import { Avatar } from '@classity-edtech/ui-base/dist/components/Displays/Avatar/Avatar';

const TextEditor = React.lazy(() =>
  import('@classity-edtech/ui-base/dist/components/Displays').then(mod => ({
    default: mod.TextEditor,
  })),
);

type ChatDetails = {
  avatarSrc: string;
  title: string;
  description: string;
  time: string;
  notification: string;
  isActive: boolean;
  action: () => void;
};

export interface ChatRoomProps extends MuiBoxProps {
  messages: {
    messageId: string;
    avatarSrc: string;
    status: string;
    senderName: string;
    sentAt: string;
    text: string;
    chatType: 'sender' | 'recipient';
    editAction: () => void;
    deleteAction: () => void;
  }[];
  loadingMessages: boolean;
  sendingMessages: boolean;
  allMessagesFetched: boolean;
  sendMessage: (text: string) => Promise<void>;
  fetchNewMessages: () => Promise<void>;
  groupChatDetails: ChatDetails;
  p2tChatDetails?: ChatDetails;
  studentsChatDetails: ChatDetails[];
  header: {
    title: string;
    avatarSrc: string;
    logo: JSX.Element;
  };
  editContent: {
    messageId: string;
    content: string;
  };
}

export const ChatRoom = ({
  className,
  messages,
  loadingMessages,
  sendingMessages,
  allMessagesFetched,
  sendMessage,
  fetchNewMessages,
  groupChatDetails,
  p2tChatDetails,
  studentsChatDetails,
  header,
  editContent,
}: ChatRoomProps) => {
  const messageSectionEndRef = useRef<HTMLDivElement | null>(null);
  const messageSectionStartRef = useRef<HTMLDivElement | null>(null);
  const screens = useAppMediaQuery();
  const isSmallScreen = screens.isMobile || screens.isTablet;
  const [showMessages, setShowMessages] = useState(false);
  const [openSheet, setOpenSheet] = useState(false);
  const [content, setContent] = useState('');
  const [showTextEditor, setShowTextEditor] = useState(true);
  const [currentScrolHeight, setCurrentScrolHeight] = useState(0);
  const [startObservingMore, setStartObservingMore] = useState(false);
  const [firstRenderAutoScrolled, setFirstRenderAutoScrolled] = useState(false);

  const toggleShowMessages = () => {
    setShowMessages(value => !value);
  };

  const toggleOpenSheet = () => {
    setOpenSheet(value => !value);
  };

  const handleSendMessage = async () => {
    if (!content) return;

    await sendMessage(content);

    setContent('');
    setShowTextEditor(false);
    setCurrentScrolHeight(0);

    handleScrollToBottom();

    setTimeout(() => {
      setShowTextEditor(true);
    }, 100);
  };

  const participants = {
    groupChat: {
      ...groupChatDetails,
      onClick: () => {
        groupChatDetails.action();
        toggleShowMessages();
      },
    },
    tutorChat: p2tChatDetails
      ? {
          ...p2tChatDetails,
          onClick: () => {
            p2tChatDetails.action();
            toggleShowMessages();
          },
        }
      : undefined,
    peersChat: studentsChatDetails?.map(d => ({
      ...d,
      onClick: () => {
        d.action();
        toggleShowMessages();
      },
    })),
  };

  const handleScrollToBottom = useCallback(() => {
    setTimeout(() => {
      messageSectionEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 20);
  }, []);

  const floatingControls = {
    video: {
      action: () => {},
      isActive: false,
    },
    audio: {
      action: () => {},
      isActive: true,
    },
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!allMessagesFetched && !loadingMessages) {
            fetchNewMessages();
            setCurrentScrolHeight(document?.querySelector?.('#chat-container')?.scrollHeight || 0);
          }
        }
      },
      { threshold: 0.1 },
    );

    if (messageSectionStartRef.current && startObservingMore) {
      observer.observe(messageSectionStartRef.current);
    }

    return () => {
      if (messageSectionStartRef.current) {
        observer.unobserve(messageSectionStartRef.current);
      }
    };
  }, [startObservingMore, allMessagesFetched, loadingMessages]);

  useEffect(() => {
    handleScrollToBottom();
  }, [handleScrollToBottom, messages.length]);

  useEffect(() => {
    if (messages?.length && !loadingMessages && currentScrolHeight > 0) {
      setTimeout(() => {
        document.querySelector('#chat-container').scrollTop =
          document?.querySelector?.('#chat-container')?.scrollHeight - currentScrolHeight;
      }, 520);
    }
  }, [messages, loadingMessages, currentScrolHeight]);

  useEffect(() => {
    if (messages?.length && !firstRenderAutoScrolled) {
      setTimeout(() => {
        messageSectionEndRef.current?.scrollIntoView();
        setStartObservingMore(true);
        setFirstRenderAutoScrolled(true);
      }, 20);
    }
  }, [messages, firstRenderAutoScrolled]);

  useEffect(() => {
    if (editContent?.content) {
      setContent(editContent?.content);
      setShowTextEditor(false);

      setTimeout(() => {
        setShowTextEditor(true);
      }, 100);
    }
  }, [editContent]);

  return (
    <Box sx={{ display: 'flex' }}>
      {(!isSmallScreen || !showMessages) && (
        <Participants
          sx={{
            borderRight: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
            height: '100dvh',
            width: { xs: '100%', md: 'max-content' },
          }}
          {...participants}
        />
      )}
      {(!isSmallScreen || showMessages) && (
        <Stack sx={{ justifyContent: 'space-between', width: '100%', position: 'relative' }}>
          <Box
            sx={{
              width: '100%',
              height: '66px',
              borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
            }}
          >
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {header?.avatarSrc ? (
                  <Avatar src={header?.avatarSrc} sx={{ width: '48px', height: '48px' }} />
                ) : (
                  header?.logo
                )}
                <Stack sx={{ gap: 0.5, justifyContent: 'space-between' }}>
                  <Typography
                    variant='body2SemiBold'
                    sx={
                      isSmallScreen
                        ? {
                            whiteSpace: 'nowrap',
                            maxWidth: '100px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }
                        : {}
                    }
                  >
                    {header?.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DashboardIcon sx={{ width: '14px', height: '14px' }} />
                    <Typography variant='body3'>Follow</Typography>
                  </Box>
                </Stack>
              </Box>
              <Stack sx={{ gap: '1.5', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <MoreIcon
                  sx={{
                    transform: 'rotate(90deg)',
                    color: globalColours['$global-colour-interactive-secondary-default'],
                  }}
                />
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='body3SemiBold'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: globalColours['$global-colour-interactive-secondary-active'],
                      px: { xs: 1, md: 2 },
                      cursor: 'pointer',
                    }}
                    onClick={isSmallScreen ? toggleOpenSheet : () => {}}
                  >
                    {isSmallScreen && (
                      <ArrowLeftIcon
                        sx={{ width: '14px', height: '14px', display: { xs: 'block', md: 'none' } }}
                      />
                    )}
                    Chat
                  </Typography>
                  <Typography
                    variant='body3SemiBold'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: globalColours['$global-colour-interactive-secondary-default'],
                      px: { xs: 1, md: 2 },
                      cursor: 'pointer',
                    }}
                  >
                    <ArrowLeftIcon
                      sx={{ width: '14px', height: '14px', transform: 'rotate(-90deg)' }}
                    />
                    Worksheet #1
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Stack
            sx={{
              height: { xs: 'calc(100dvh - 128px)', md: 'calc(100dvh - 206px)' },
              overflow: 'auto',
              px: { xs: 2.5, md: 1 },
              py: 3,
              gap: 1.5,
            }}
            id='chat-container'
          >
            <div ref={messageSectionStartRef}></div>

            {loadingMessages && <CircularProgress size={24} />}

            <>
              {messages?.map(message => <ChatBubble key={message.messageId} {...message} />)}

              {false && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    p: 1.5,
                    borderRadius: '16px',
                    border: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                    width: 'fit-content',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: globalColours['$global-colour-content-tertiary'],
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                    }}
                  ></Box>
                  <Box
                    sx={{
                      backgroundColor: globalColours['$global-colour-content-tertiary'],
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                    }}
                  ></Box>
                  <Box
                    sx={{
                      backgroundColor: globalColours['$global-colour-content-tertiary'],
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                    }}
                  ></Box>
                </Box>
              )}
            </>
            <div ref={messageSectionEndRef}></div>
          </Stack>

          <Box
            sx={{
              height: { sm: '46px', md: '140px' },
              px: { xs: 2.5, md: 0 },
              py: { xs: 1, md: 0 },
            }}
          >
            {typeof window !== 'undefined' && showTextEditor && (
              <Suspense fallback={<div>Loading editor...</div>}>
                <TextEditor
                  content={content}
                  setContent={setContent}
                  sendMessage={loadingMessages ? () => {} : () => handleSendMessage()}
                  sendingMessages={sendingMessages}
                />
              </Suspense>
            )}
          </Box>

          <Sheet
            closeSheet={toggleOpenSheet}
            openSheet={openSheet}
            body={
              <Participants sx={{ width: { xs: '100%', md: 'max-content' } }} {...participants} />
            }
          />
        </Stack>
      )}
      <FloatingControl {...floatingControls} />
    </Box>
  );
};

ChatRoom.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ChatRoom.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(ChatRoom)(
  ({ theme }) => `
`,
);
